export const LOG_LEVELS = ["ERROR", "WARN", "INFO", "DEBUG"];

export interface LogsPathQuery {
  start?: Date;
  end?: Date;
  traceId?: string;
  levels?: string[];
}

/**
 * Parses the new query path format, a comma separated list of key=value pairs:
 *
 *   from=2024-01-01T10:00,to=2024-01-01T11:00,traceId=abc123,level=ERROR
 *
 * All parts are optional, and `level` may be repeated to select several log
 * levels. Returns null if the query contains an unknown key, a malformed pair,
 * an unknown log level or an unparsable date.
 */
export function parseLogsPathQuery(query: string): LogsPathQuery | null {
  const result: LogsPathQuery = {};
  const levels: string[] = [];

  for (const part of query.split(",")) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }
    const separatorIndex = trimmed.indexOf("=");
    if (separatorIndex < 1) {
      return null;
    }
    const key = trimmed.substring(0, separatorIndex).trim();
    const value = trimmed.substring(separatorIndex + 1).trim();
    if (!value) {
      return null;
    }

    switch (key) {
      case "from": {
        const start = new Date(value);
        if (isNaN(start.getTime())) {
          return null;
        }
        result.start = start;
        break;
      }
      case "to": {
        const end = new Date(value);
        if (isNaN(end.getTime())) {
          return null;
        }
        result.end = end;
        break;
      }
      case "traceId":
        result.traceId = value;
        break;
      case "level": {
        const level = value.toUpperCase();
        if (!LOG_LEVELS.includes(level)) {
          return null;
        }
        if (!levels.includes(level)) {
          levels.push(level);
        }
        break;
      }
      default:
        return null;
    }
  }

  if (levels.length > 0) {
    result.levels = levels;
  }
  return result;
}
